import { AppData, Lightning, Log, Utils } from '@lightningjs/sdk'
import { Helper } from '../lib/helper'
import { ItemNode } from "../lib/components/grid/ItemNode"
import StarUI from './star-ui'
import { Artwork, Badge } from '@lightningjs/ui-components'
import { AppApiConfig } from '../lib/app-api-config'
import { AppSetup } from '../lib/app-setup'
import FallbackImage from './fallback-image'
import WebpTexture from '../ui-textures/wepp-texture'

export interface InfoUITemplateSpec extends Lightning.Component.TemplateSpec {
    NodeImg: object
    ImageWrapper: object
    InfoTextWrapper: Object
    background: string
    item: ItemNode
    isMlpScreen: boolean
}

export interface Theme {
    ageRatingColor: string;
    titleColor: string;
    textSize: number;
    textcolor: string;
    gridfocuscolor: string;
    bgcolor: string;
}

export default class InfoUI
    extends Lightning.Component<InfoUITemplateSpec>
    implements Lightning.Component<InfoUITemplateSpec>
{
    NodeImg = this.getByRef('NodeImg')!
    ImageWrapper = this.getByRef('ImageWrapper')!
    private _focusAnimation: Lightning.types.Animation | undefined = undefined
    _theme: Theme = {};
    currentItem: ItemNode | null = null
    pendingLogoUrl: string | null = null
    debounceId = null
    _isMlpScreen = false
    static override _template(): Lightning.Component.Template<InfoUITemplateSpec> {
        return {
            NodeImg: {
                w: 1244,
                h: 700,
                x: 1920,
                y: 0,
                type: Artwork,
                src: '',
                mountX: 1,
            },
            ImageWrapper: {
                w: (w) => w,
                h: (h) => h,
                texture: '',
            },
            InfoTextWrapper: {
                x: 172,
                y: 136,
                flex: { direction: 'column' },
                SeriesTitle: { text : {text: '',  fontSize: 38, fontFace: Helper.getFontFamily('Regular') }},
                Title: { text: { text: '', fontFace: Helper.getFontFamily('Black'), fontSize: 42, wordWrapWidth: 660, maxLines: 2, maxLinesSuffix: '...', textOverflow: 'ellipsis' }, color: '' },
                ContentLogo: { 
                    visible: false,
                    flexItem: {
                        // marginTop: 5,
                        marginBottom: 20
                    }
                },
                ItemMetadataWrapper: {
                    flex: { direction: 'row', alignItems: 'center', wrap: false },
                    rect: true, color: 0x00000000,
                    children: []
                },
                Description: { y: 10, text: { text: '', fontSize: 24, fontFace: Helper.getFontFamily('Regular'), wordWrapWidth: 600, maxLines: 3, maxLinesSuffix: '...', textOverflow: 'ellipsis', lineHeight: 36, letterSpacing: 0.5 } },
                MoodsAndMoreWrapper: {
                    y: 10,
                    w: 500,
                    flex: { direction: 'row', alignItems: 'center', justifyContent: 'space-between' },
                    MoodsTag: {
                        flexItem: { marginRight: 35 },
                        text: { text: '', fontSize: 24, fontFace: Helper.getFontFamily('Light'), textColor: 0x80ffffff }
                    },
                    MoreOptionsCTA: {
                        flex: { direction: 'row', alignItems: 'center' },
                        Prefix: {
                            text: { text: '', fontSize: 24, fontFace: Helper.getFontFamily('Light'), textColor: 0x80ffffff }
                        },
                        Icon: {
                            x: -7,
                            texture: { src: '', resizeMode: { h: 36, w: 36, type: 'contain' }, type: Lightning.textures.ImageTexture }
                        },
                    },
                },
            }
        }

    }
    override _init() {
        // const image = this.tag('NodeImg')
        // image?.on('txLoaded', () => {
        //   image.setSmooth('alpha', 1)
        // })
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        this._theme = (app?.themesObj as Theme) || {};
        const apiConfig: AppApiConfig = appState?.getAppApiConfig()!
        const appApiConfig = appState?.getAppApiConfig()
        this._theme.titleColor = `0xff${appApiConfig?.appConfig?.titleColor}` || '0xffffffff'
        this._theme.ageRatingColor = `0xff${appApiConfig?.appConfig?.ageRatingColor}` || 'b0xffffffff'

        this.tag('InfoTextWrapper.ContentLogo').on('txError', this._onContentLogoError.bind(this))
    }

    /* Fallback: if the ContentLogo image errors (e.g. 404), show the text Title instead */
    _onContentLogoError() {
        if (!this.pendingLogoUrl || !this.currentItem) return
        const item = this.currentItem
        Log.error('ContentLogo failed to load, falling back to title:', this.pendingLogoUrl)
        this.pendingLogoUrl = null
        this.patch({
            InfoTextWrapper: {
                Title: { visible: true, text: { text: item.title }, color: this._theme.titleColor },
                ContentLogo: { visible: false },
            },
        })
    }
    set background(url: string) {
        this.patch({
            ImageWrapper: {
                texture: {
                    src: url,
                    resizeMode: {
                        h: 1080,
                        w: 1920,
                        type: 'contain',
                    },
                    type: WebpTexture
                },
            },
        })
    }

    /**
    * @method gridVideoPlaying
    * @description Set the grid video playing state.
    * @param {boolean} value - The value to set for the grid video playing state.
    * @returns {void}
    *
    */
    set gridVideoPlaying(value: boolean) {
        this.patch({
            NodeImg: {
                alpha: value ? 0 : 1,
            }
        })
    }

    set showDetails(visible: boolean) {
        this.tag('InfoTextWrapper')?.smooth = { alpha: visible ? 1 : 0 }
    }

    set isMlpScreen(v: boolean) {
        this._isMlpScreen = v
        if (v) {
            this.tag('InfoTextWrapper')?.patch({
                y: 55,
                x: 152,
            })
        }
    }

    get isMlpScreen() {
        return this._isMlpScreen
    }

    getMoreOptionsPatch(isLive: boolean) {
        if(isLive) {
            return {
                Prefix: {
                    text: { text: ''}
                },
                Icon: {
                    texture: ''
                },
                // Suffix: {
                //     text: { text: ''}
                // }
            }
        }
        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig();
        var optionsIcon = appApiConfig?.appLayoutConfig.optionsIcon || ''
        return {
            Prefix: {
                text: { text: 'more'}
            },
            Icon: {
                texture: {
                    resizeMode: {type: 'cover', w: 40, h: 40},
                    x:0, y:6,w:60,h:60,
                    type: Lightning.textures.ImageTexture, src: platform ? platform.getOptionsIconPath(optionsIcon) : ''
                }
            },
            // Suffix: {
            //     text: { text: ' for more...'}
            // }
        }
    }

    getPatchObjects(item: ItemNode): any {
       // console.log('item.title', item);
        const { appState, platform } = AppData as AppSetup;
        const appApiConfig: AppApiConfig = appState?.getAppApiConfig()!;
        const bgcolor = Helper.getHexaColor(appApiConfig?.appLayoutConfig.bgcolor)
        const textcolor = Helper.getHexaColor(appApiConfig?.appLayoutConfig.textcolor,0.5)
        const likeImg = appApiConfig?.appConfig.likeImg;
        const dislikeImg = appApiConfig?.appConfig.dislikeImg;
        const loveitImg = appApiConfig?.appConfig.loveitImg
        const isLive = item.feed_type === 'live';
        const infoObj: any = {
            SeriesTitle: { text: { text: (this._isMlpScreen && item.series_name ? item.series_name : '') }},
            ItemMetadataWrapper: {
                children: []
            },
            Description: {
                text: { text: Helper.getLocalizedDescription(item) },
            },
            MoodsAndMoreWrapper: {
                MoodsTag: { text: { text: item.mood_tags ? item.mood_tags.split(',').join('  •  ') : ''}},
                MoreOptionsCTA: {
                    ...this.getMoreOptionsPatch(isLive)
                },
            }
        }

        this.currentItem = item
        const contentLogoImg = item.content_logo
        if (appApiConfig?.appConfig?.enableContentLogo && contentLogoImg) {
            this.pendingLogoUrl = contentLogoImg
            infoObj.Title = { visible: false }
            infoObj.ContentLogo = {
                visible: true,
                texture: {
                    src: contentLogoImg,
                    type: Lightning.textures.ImageTexture,
                    resizeMode: { type: 'contain', h: 70 }
                },
            }
        } else {
            this.pendingLogoUrl = null
            infoObj.Title = { visible: true, text: { text: item.title }, color: this._theme.titleColor }
            infoObj.ContentLogo = { visible: false }
        }

        const runtimeAlpha = (item.runtime == 0 ? 0 : 1)
        const airingPeriodAlpha = (item.airing_period == null || item.airing_period === '' ? 0 : 1)
        const ageRatingAlpha = (item.age_appropriate_rating == '' ? 0 : 1)
        const popularityRatingAlpha = (item.popularity_rating == -1 ? 0 : 1)
        const releaseDate = item.release_date
        const actorsAlpha = (item.actors == null ? 0 : 1)
        const likeDisLikeAlpha = (item.likedislike == null ? 0 : Number(item.likedislike))
        const directorsAlpha = (item.directors == null ? 0 : 1)
        const releaseDateRuntime = [
            releaseDate !== '' ? releaseDate : null,
            runtimeAlpha == 1 ? Helper.secondsToHms(item.runtime) : null,
        ].filter((v): v is string => v !== null).join(' • ')
        if (releaseDateRuntime !== '' && airingPeriodAlpha == 0) {
            infoObj.ItemMetadataWrapper.children.push({
                h: 50,
                y: 8,
                flexItem: { marginLeft: 0, marginRight: 35 },
                text: {  text: releaseDateRuntime, fontSize: 24, fontFace: Helper.getFontFamily('Light'), textColor: Helper.getHexaColor("ffffff", 0.5) },
            })
        }
        if (airingPeriodAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                h: 40,
                y: 6,
                flexItem: { marginLeft: 0, marginRight: 35 },
                text: { text: item.airing_period, fontSize: 24, fontFace: Helper.getFontFamily('Light'), textColor: Helper.getHexaColor("ffffff", 0.5)},
            })
        }
        if (ageRatingAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                // h: 50,
                flexItem: { marginLeft: 0, marginRight: 35 },
                type: Badge,
                title: item.age_appropriate_rating, style: { paddingX: 16, paddingY: 3, strokeWidth: 0, backgroundColor: textcolor, radius: 0, textStyle: { fontSize: 20, textColor: bgcolor, fontFace: Helper.getFontFamily('Regular') } }
                //text: { text: item.age_appropriate_rating, fontSize: 26, fontFace: 'Light', textAlign: 'center', paddingLeft: 10, paddingRight: 10, textColor: this._theme.ageRatingColor },
            })
        }

         const ccIcon = Helper.getCCIcon(item);
        if (ccIcon !== '') {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                color: Helper.getHexaColor("ffffff", 0.5),
                texture: {
                    src: Utils.asset(ccIcon),
                    type: Lightning.textures.ImageTexture
                }
            })
        }

        const isMultiLangEnabled = Helper.hasSupportedMultiLangDescription(item);

        if (isMultiLangEnabled) {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                color: Helper.getHexaColor("ffffff", 0.5),
                texture: {
                    src: Utils.asset('images/ico-multi-lang.png'),
                    type: Lightning.textures.ImageTexture
                }
            })
        }

        if (likeDisLikeAlpha !== 0) {
            let icon = '';
            let isApiIcon = false;
            if (likeDisLikeAlpha == 1) {
                icon = 'images/ico-thumbsup.png';
                // isApiIcon = !!likeImg;
            }
            else if (likeDisLikeAlpha == 2) {
                icon = 'images/ico-love.png';
                // isApiIcon = !!loveitImg;
            }
            else if (likeDisLikeAlpha == 3) {
                icon =  'images/ico-thumbsdown.png';
                // isApiIcon = !!dislikeImg;
            }

            if (icon !== '') {
                infoObj.ItemMetadataWrapper.children.push({
                    w: 36, h: 36,
                    flexItem: { marginLeft: 0, marginRight: 35 },
                    alpha: 0.5,
                    type: FallbackImage,
                    imgSrc: icon,
                })
            }
        }

        if (popularityRatingAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                // h: 40,
                color: popularityRatingAlpha,
                type: StarUI,
                rating: item.popularity_rating,
            })
        }
        // if (actorsAlpha == 1) {
        //   infoObj.Actors = {
        //     alpha: actorsAlpha,
        //     text: { text: 'Cast: ' + item.actors, fontSize: 24, fontFace: 'Light' },
        //   }
        // }
        // if (directorsAlpha == 1) {
        //   infoObj.Directors = {
        //     alpha: directorsAlpha,
        //     text: { text: 'Direction: ' + item.directors, fontSize: 24, fontFace: 'Light' },
        //   }
        // }

        return infoObj
    }

    imageLoaded() {
        this.NodeImg.blur = false
        this.NodeImg.signals = {}
    }

    set item(item: ItemNode) {
        if (!item) return;
        this.patch({
            InfoTextWrapper: this.getPatchObjects(item),
        })

        let src = item.hero_image == '' ? item.bg_image_landscape : item.hero_image
        //XFIN-731 - This issue happends when the grid focus is chaged and refocus happends
        //as it do not fire the 'imageLoaded' event if the src is same.
        // hence add a check if the src url is different then only image is reloaded.
        if (src !== this.NodeImg.src) {
            this.NodeImg.blur = true
            clearTimeout(this.debounceId);
            this.debounceId = setTimeout(() => {
                this.NodeImg.src = src
                this.NodeImg.signals = {
                    'imageLoaded': this.imageLoaded.bind(this)
                }
            }, 600)
        }
    }
}
